import React from 'react';
import { Target, Heart, Award, TrendingUp } from 'lucide-react';
import { attorneys } from '../mockData';
import { Card } from '../components/ui/card';
import { MobileCarousel } from '../components/MobileCarousel';
import { Button } from '../components/ui/button';
import { openZendeskChat } from '../utils/zendesk';

export const Attorneys = ({ onOpenPopup }) => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-[#58225a] text-white py-20 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMGhhbmRzaGFrZSUyMGRlYWx8ZW58MHx8fHwxNzc2Mjg0ODk2fDA&ixlib=rb-4.1.0&q=85"
            alt="Professional team"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#58225a]/80"></div>
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">Our Attorneys</h1>
            <p className="text-xl md:text-2xl text-gray-300">
              Meet our experienced team of legal professionals dedicated to protecting your intellectual property.
            </p>
          </div>
        </div>
      </section>

      {/* Attorneys Grid - Different Layout */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="flex flex-col gap-8 max-w-6xl mx-auto">
            {attorneys.map((attorney) => (
              <Card
                key={attorney.id}
                className="relative p-0 border-2 border-[#e8a8c8] shadow-xl overflow-hidden transition-all duration-300 hover:shadow-2xl hover:border-[#ba2a84]"
                style={{ borderRadius: '2rem' }}
              >
                <div className="flex flex-col md:flex-row">
                  {/* Left panel */}
                  <div
                    className="md:w-1/3 flex flex-col items-center justify-center text-center p-8"
                    style={{ background: 'linear-gradient(135deg, #ba2a84 0%, #58225a 100%)' }}
                  >
                    <div className="w-20 h-20 rounded-full bg-[#e8a8c8]/20 flex items-center justify-center mb-4">
                      <Award className="h-10 w-10 text-[#e8a8c8]" />
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-2">{attorney.name}</h3>
                    {attorney.barId && (
                      <p className="text-white/80 text-sm">
                        <span className="font-semibold text-[#e8a8c8]">Bar ID:</span> {attorney.barId}
                      </p>
                    )}
                  </div>

                  {/* Right panel */}
                  <div className="md:w-2/3 p-8 bg-white flex flex-col justify-center">
                    <div className="flex flex-wrap gap-6 mb-4">
                      {attorney.admitted && (
                        <p className="text-gray-700 text-sm">
                          <span className="font-semibold text-[#ba2a84]">Admitted:</span> {attorney.admitted}
                        </p>
                      )}
                      {attorney.school && (
                        <p className="text-gray-700 text-sm">
                          <span className="font-semibold text-[#ba2a84]">School:</span> {attorney.school}
                        </p>
                      )}
                      {attorney.firm && (
                        <p className="text-gray-700 text-sm">
                          <span className="font-semibold text-[#ba2a84]">Firm:</span> {attorney.firm}
                          {attorney.location && <span className="ml-1 text-gray-500">• {attorney.location}</span>}
                        </p>
                      )}
                    </div>

                    <p className="text-[#ba2a84] text-sm font-semibold mb-2">Specialties:</p>
                    <div className="flex flex-wrap gap-2">
                      {attorney.specialties.map((specialty, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 rounded-full text-xs font-medium bg-[#f4e5c5] text-[#58225a]"
                        >
                          {specialty}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* The Principles That Guide Our Work */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-[#58225a] mb-4">The Principles That Guide Our Work</h2>
            <p className="text-lg text-gray-600">These core values influence every service we provide and every client relationship we build.</p>
          </div>
          <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {[
              { icon: Target, t: "Accuracy", d: "Every trademark application is carefully prepared to meet USPTO requirements and maximize filing accuracy." },
              { icon: Heart, t: "Client Focus", d: "We understand the hard work, dedication, and vision behind every brand we help protect." },
              { icon: Award, t: "Transparency", d: "We provide honest feedback about trademark eligibility and potential challenges before moving forward." },
              { icon: TrendingUp, t: "Efficiency", d: "Our streamlined systems and organized workflows help reduce delays and keep projects moving smoothly." }
            ].map((v, i) => (
              <Card key={i} className="p-8 text-center hover:shadow-xl transition-shadow">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-6"><v.icon className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-[#58225a] mb-4">{v.t}</h3>
                <p className="text-gray-600">{v.d}</p>
              </Card>
            ))}
          </div>
          <MobileCarousel>
            {[
              { icon: Target, t: "Accuracy", d: "Every trademark application is carefully prepared to meet USPTO requirements and maximize filing accuracy." },
              { icon: Heart, t: "Client Focus", d: "We understand the hard work, dedication, and vision behind every brand we help protect." },
              { icon: Award, t: "Transparency", d: "We provide honest feedback about trademark eligibility and potential challenges before moving forward." },
              { icon: TrendingUp, t: "Efficiency", d: "Our streamlined systems and organized workflows help reduce delays and keep projects moving smoothly." }
            ].map((v, i) => (
              <Card key={i} className="p-8 text-center shadow-lg">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-primary text-white mb-6"><v.icon className="h-8 w-8" /></div>
                <h3 className="text-xl font-bold text-[#58225a] mb-4">{v.t}</h3>
                <p className="text-gray-600">{v.d}</p>
              </Card>
            ))}
          </MobileCarousel>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 text-white overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1772541800741-0ff177b2c282?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMGNpdHlzY2FwZSUyMHNreWxpbmUlMjBuaWdodCUyMGRhcmt8ZW58MHx8fHwxNzc2Mjg2OTQzfDA&ixlib=rb-4.1.0&q=85" alt="City skyline at dusk" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#58225a]/80"></div>
        </div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-4xl font-bold mb-6">Start Protecting Your Brand Today</h2>
          <p className="text-xl mb-8 text-gray-300 max-w-2xl mx-auto">
            Join the many businesses that trust Trademark Examining Lawyer to safeguard their intellectual property. Begin your trademark application today.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" onClick={onOpenPopup} className="bg-[#ba2a84] hover:bg-[#8a1f63] text-white text-lg px-8 py-6 font-semibold">
              File Your Trademark
            </Button>
            <Button size="lg" variant="outline" onClick={openZendeskChat} className="js-chat border-2 border-white text-white hover:bg-white hover:text-[#58225a] text-lg px-8 py-6 font-semibold">
              Start a Chat
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
